import supabase from "./client";

/**
 * Lookup Service - Contains all Supabase queries for lookup tables
 * These are reference tables that are used for filtering and displaying options
 */

// ==================== GENRES ====================

/**
 * Get all genres
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getAllGenres = async () => {
  try {
    const { data, error } = await supabase
      .from("genres")
      .select("id, genres")
      .order("id", { ascending: false });

    if (error) throw error;

    // Create a map for easy lookup
    const genreMap = {};
    data.forEach((g) => {
      genreMap[g.id] = g.genres;
    });

    return { data, genreMap, error: null };
  } catch (error) {
    console.error("Error fetching genres:", error);
    return { data: [], genreMap: {}, error };
  }
};

// ==================== LANGUAGES ====================

/**
 * Get all languages
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getAllLanguages = async () => {
  try {
    const { data, error } = await supabase
      .from("languages")
      .select("id, languages");

    if (error) throw error;

    // Create a map for easy lookup
    const languageMap = {};
    data.forEach((lang) => {
      languageMap[lang.id] = lang.languages;
    });

    return { data, languageMap, error: null };
  } catch (error) {
    console.error("Error fetching languages:", error);
    return { data: [], languageMap: {}, error };
  }
};

// ==================== SOURCES ====================

/**
 * Get all sources
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getAllSources = async () => {
  try {
    const { data, error } = await supabase
      .from("source")
      .select("id, source");

    if (error) throw error;

    // Create a map for easy lookup
    const sourceMap = {};
    data.forEach((s) => {
      sourceMap[s.id] = s.source;
    });

    return { data, sourceMap, error: null };
  } catch (error) {
    console.error("Error fetching sources:", error);
    return { data: [], sourceMap: {}, error };
  }
};

// ==================== CAST MEMBERS ====================

/**
 * Get all cast members
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getAllCastMembers = async () => {
  try {
    const { data, error } = await supabase
      .from("castMembers")
      .select("id, castMembers");

    if (error) throw error;

    // Create a map for easy lookup
    const castMap = {};
    data.forEach((c) => {
      castMap[c.id] = c.castMembers;
    });

    return { data, castMap, error: null };
  } catch (error) {
    console.error("Error fetching cast members:", error);
    return { data: [], castMap: {}, error };
  }
};

/**
 * Search cast members with pagination for AsyncPaginate
 * @param {string} search - Search term
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - {page: number}
 * @returns {Object} {options, hasMore, additional}
 */
export const searchCastMembers = async (search, loadedOptions, additional) => {
  const page = additional.page || 1;
  const pageSize = 30;

  try {
    let query = supabase
      .from("castMembers")
      .select("id, castMembers")
      .order("castMembers", { ascending: true })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (search && search.trim()) {
      query = query.ilike("castMembers", `%${search.trim()}%`);
    }

    const { data, error, count } = await query;

    if (error) {
      console.error("Error searching cast members:", error);
      return {
        options: [],
        hasMore: false,
        additional: { page }
      };
    }

    const options = data.map((item) => ({
      label: item.castMembers,
      value: item.id
    }));

    // hasMore: full page loaded
    const hasMore = data.length === pageSize;

    return {
      options,
      hasMore,
      additional: { page: page + 1 }
    };
  } catch (error) {
    console.error("Unexpected error in searchCastMembers:", error);
    return {
      options: [],
      hasMore: false,
      additional: { page }
    };
  }
};

/**
 * Search genres with pagination for AsyncPaginate (NEW)
 * @param {string} search - Search term
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - {page: number}
 * @returns {Object} {options, hasMore, additional}
 */
export const searchGenres = async (search, loadedOptions, additional) => {
  const page = additional.page || 1;
  const pageSize = 30;

  try {
    let query = supabase
      .from("genres")
      .select("id, genres")
      .order("genres", { ascending: true })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (search && search.trim()) {
      query = query.ilike("genres", `%${search.trim()}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error searching genres:", error);
      return {
        options: [],
        hasMore: false,
        additional: { page }
      };
    }

    const options = data.map((item) => ({
      label: item.genres,
      value: item.id
    }));

    const hasMore = data.length === pageSize;

    return {
      options,
      hasMore,
      additional: { page: page + 1 }
    };
  } catch (error) {
    console.error("Unexpected error in searchGenres:", error);
    return {
      options: [],
      hasMore: false,
      additional: { page }
    };
  }
};

/**
 * Search languages with pagination for AsyncPaginate (NEW)
 * @param {string} search - Search term
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - {page: number}
 * @returns {Object} {options, hasMore, additional}
 */
export const searchLanguages = async (search, loadedOptions, additional) => {
  const page = additional.page || 1;
  const pageSize = 30;

  try {
    let query = supabase
      .from("languages")
      .select("id, languages")
      .order("languages", { ascending: true })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (search && search.trim()) {
      query = query.ilike("languages", `%${search.trim()}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error searching languages:", error);
      return {
        options: [],
        hasMore: false,
        additional: { page }
      };
    }

    const options = data.map((item) => ({
      label: item.languages,
      value: item.id
    }));

    const hasMore = data.length === pageSize;

    return {
      options,
      hasMore,
      additional: { page: page + 1 }
    };
  } catch (error) {
    console.error("Unexpected error in searchLanguages:", error);
    return {
      options: [],
      hasMore: false,
      additional: { page }
    };
  }
};

/**
 * Search sources with pagination for AsyncPaginate (NEW)
 * @param {string} search - Search term
 * @param {Array} loadedOptions - Previously loaded options
 * @param {Object} additional - {page: number}
 * @returns {Object} {options, hasMore, additional}
 */
export const searchContentTypes = async (search, loadedOptions, additional) => {
  const page = additional.page || 1;
  const pageSize = 30;

  try {
    let query = supabase
      .from("contentType")
      .select("id, contentType")
      .order("contentType", { ascending: true })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (search && search.trim()) {
      query = query.ilike("contentType", `%${search.trim()}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error searching content types:", error);
      return {
        options: [],
        hasMore: false,
        additional: { page }
      };
    }

    const options = data.map((item) => ({
      label: item.contentType,
      value: item.id
    }));

    const hasMore = data.length === pageSize;

    return {
      options,
      hasMore,
      additional: { page: page + 1 }
    };
  } catch (error) {
    console.error("Unexpected error in searchContentTypes:", error);
    return {
      options: [],
      hasMore: false,
      additional: { page }
    };
  }
};

export const searchSources = async (search, loadedOptions, additional) => {
  const page = additional.page || 1;
  const pageSize = 30;

  try {
    let query = supabase
      .from("source")
      .select("id, source")
      .order("source", { ascending: true })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (search && search.trim()) {
      query = query.ilike("source", `%${search.trim()}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error searching sources:", error);
      return {
        options: [],
        hasMore: false,
        additional: { page }
      };
    }

    const options = data.map((item) => ({
      label: item.source,
      value: item.id
    }));

    const hasMore = data.length === pageSize;

    return {
      options,
      hasMore,
      additional: { page: page + 1 }
    };
  } catch (error) {
    console.error("Unexpected error in searchSources:", error);
    return {
      options: [],
      hasMore: false,
      additional: { page }
    };
  }
};





export const searchDirectors = async (search, loadedOptions, additional) => {
  const page = additional.page || 1;
  const pageSize = 30;

  try {
    let query = supabase
      .from("directors")
      .select("id, directors")
      .order("directors", { ascending: true })
      .range((page - 1) * pageSize, page * pageSize - 1);

    if (search && search.trim()) {
      query = query.ilike("directors", `%${search.trim()}%`);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error searching directors:", error);
      return {
        options: [],
        hasMore: false,
        additional: { page }
      };
    }

    const options = data.map((item) => ({
      label: item.directors,
      value: item.id
    }));

    const hasMore = data.length === pageSize;

    return {
      options,
      hasMore,
      additional: { page: page + 1 }
    };
  } catch (error) {
    console.error("Unexpected error in searchDirectors:", error);
    return {
      options: [],
      hasMore: false,
      additional: { page }
    };
  }
};

// ==================== DIRECTORS ====================

/**
 * Get all directors
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getAllDirectors = async () => {
  try {
    const { data, error } = await supabase
      .from("directors")
      .select("id, directors");

    if (error) throw error;

    // Create a map for easy lookup
    const directorMap = {};
    data.forEach((d) => {
      directorMap[d.id] = d.directors;
    });

    return { data, directorMap, error: null };
  } catch (error) {
    console.error("Error fetching directors:", error);
    return { data: [], directorMap: {}, error };
  }
};

// ==================== CONTENT TYPES ====================

/**
 * Get all content types
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getAllContentTypes = async () => {
  try {
    const { data, error } = await supabase
      .from("contentType")
      .select("id, contentType")
      .order("id", { ascending: false });

    if (error) throw error;

    // Format for React Select
    const formatted = data.map((ct) => ({
      value: ct.id,
      label: ct.contentType,
    }));

    return { data, formatted, error: null };
  } catch (error) {
    console.error("Error fetching content types:", error);
    return { data: [], formatted: [], error };
  }
};

// ==================== YEARS ====================

/**
 * Get all years
 * @returns {Promise<{data: Array, error: Error|null}>}
 */
export const getAllYears = async () => {
  try {
    const { data, error } = await supabase
      .from("year")
      .select("id, year")
      .order("year", { ascending: false });

    if (error) throw error;

    // Create a map for easy lookup
    const yearMap = {};
    data.forEach((y) => {
      yearMap[y.id] = y.year;
    });

    return { data, yearMap, error: null };
  } catch (error) {
    console.error("Error fetching years:", error);
    return { data: [], yearMap: {}, error };
  }
};

// ==================== BATCH LOOKUP ====================

/**
 * Get all lookup data in a single call
 * @returns {Promise<{lookupData: Object, error: Error|null}>}
 */
export const getAllLookupData = async () => {
  try {
    const [
      genresResult,
      languagesResult,
      sourcesResult,
      castMembersResult,
      directorsResult,
      contentTypesResult,
      yearsResult,
    ] = await Promise.all([
      getAllGenres(),
      getAllLanguages(),
      getAllSources(),
      getAllCastMembers(),
      getAllDirectors(),
      getAllContentTypes(),
      getAllYears(),
    ]);

    const lookupData = {
      genres: {
        data: genresResult.data,
        map: genresResult.genreMap,
      },
      languages: {
        data: languagesResult.data,
        map: languagesResult.languageMap,
      },
      sources: {
        data: sourcesResult.data,
        map: sourcesResult.sourceMap,
      },
      castMembers: {
        data: castMembersResult.data,
        map: castMembersResult.castMap,
      },
      directors: {
        data: directorsResult.data,
        map: directorsResult.directorMap,
      },
      contentTypes: {
        data: contentTypesResult.data,
        formatted: contentTypesResult.formatted,
      },
      years: {
        data: yearsResult.data,
        map: yearsResult.yearMap,
      },
    };

    return { lookupData, error: null };
  } catch (error) {
    console.error("Error fetching all lookup data:", error);
    return { lookupData: null, error };
  }
};

// ==================== RESOLVE LOOKUP VALUES ====================

/**
 * Resolve cast member IDs to names or vice versa
 * @param {string} value - ID or name
 * @param {Object} castMap - Cast members map
 * @returns {string} - Resolved value
 */
export const resolveCastMember = async (value, castMap) => {
  if (!value) return [];

  let arr = [];
  try {
    arr = typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    arr = Array.isArray(value) ? value : [value];
  }

  return arr.map((item) => {
    const clean = item?.toString().trim().toLowerCase();
    const byName = Object.entries(castMap).find(
      ([id, name]) => name.toString().trim().toLowerCase() === clean
    );
    if (byName) return byName[1];
    if (castMap[item]) return castMap[item];
    return item;
  });
};

/**
 * Resolve director IDs to names or vice versa
 * @param {string} value - ID or name
 * @param {Object} directorMap - Directors map
 * @returns {string} - Resolved value
 */
export const resolveDirector = async (value, directorMap) => {
  if (!value) return [];

  let arr = [];
  try {
    arr = typeof value === "string" ? JSON.parse(value) : value;
  } catch {
    arr = Array.isArray(value) ? value : [value];
  }

  return arr.map((item) => {
    const clean = item?.toString().trim().toLowerCase();
    const byName = Object.entries(directorMap).find(
      ([id, name]) => name.toString().trim().toLowerCase() === clean
    );
    if (byName) return byName[1];
    if (directorMap[item]) return directorMap[item];
    return item;
  });
};

export default {
  getAllGenres,
  getAllLanguages,
  getAllSources,
  getAllCastMembers,
  getAllDirectors,
  getAllContentTypes,
  getAllYears,
  getAllLookupData,
  resolveCastMember,
  resolveDirector,
  searchCastMembers,
  searchGenres,
  searchLanguages,
  searchSources,
};



